<template>
    <UserLayout :page-title="t('referralList')" :page-section="t('network')">
        <div class="rl-wrap">
            <div class="rl-inner">

                <!-- Welcome Strip -->
                <div class="rl-welcome anim-up" style="--d:0ms">
                    <div class="rlw-left">
                        <div class="rlw-icon">
                            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                            </svg>
                        </div>
                        <div>
                            <p class="rlw-eyebrow">{{ t('network') }}</p>
                            <h1 class="rlw-title">{{ t('referralList') }}</h1>
                        </div>
                    </div>
                    <div class="rlw-chips">
                        <div class="rlw-chip">
                            <span class="rlw-chip-val">{{ summary.total }}</span>
                            <span class="rlw-chip-lbl">{{ t('totalReferrals') }}</span>
                        </div>
                        <div class="rlw-chip rlw-chip--green">
                            <span class="rlw-chip-val">{{ summary.active }}</span>
                            <span class="rlw-chip-lbl">{{ t('active') }}</span>
                        </div>
                        <div class="rlw-chip rlw-chip--blue">
                            <span class="rlw-chip-val">{{ currencySymbol }}{{ formatCurrency(summary.totalEarnings) }}</span>
                            <span class="rlw-chip-lbl">{{ t('earned') }}</span>
                        </div>
                    </div>
                </div>

                <!-- Stat Cards -->
                <div class="rl-stats">
                    <div class="rl-stat anim-up" style="--d:60ms;--ac:167,93,237">
                        <div class="rls-glow"></div>
                        <div class="rls-icon">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                            </svg>
                        </div>
                        <p class="rls-lbl">{{ t('totalReferrals') }}</p>
                        <p class="rls-val">{{ summary.total }}</p>
                        <p class="rls-sub">{{ t('allTime') }}</p>
                        <div class="rls-bar-wrap"><div class="rls-bar shimmer-bar" style="width:100%"></div></div>
                    </div>

                    <div class="rl-stat anim-up" style="--d:100ms;--ac:52,211,153">
                        <div class="rls-glow"></div>
                        <div class="rls-icon">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                        </div>
                        <p class="rls-lbl">{{ t('activeReferrals') }}</p>
                        <p class="rls-val">{{ summary.active }}</p>
                        <p class="rls-sub">{{ t('activeMembers') }}</p>
                        <div class="rls-bar-wrap"><div class="rls-bar shimmer-bar" style="width:80%"></div></div>
                    </div>

                    <div class="rl-stat anim-up" style="--d:140ms;--ac:251,191,36">
                        <div class="rls-glow"></div>
                        <div class="rls-icon">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                        </div>
                        <p class="rls-lbl">{{ t('totalEarnings') }}</p>
                        <p class="rls-val">{{ currencySymbol }}{{ formatCurrency(summary.totalEarnings) }}</p>
                        <p class="rls-sub">{{ t('fromReferrals') }}</p>
                        <div class="rls-bar-wrap"><div class="rls-bar shimmer-bar" style="width:65%"></div></div>
                    </div>

                    <div class="rl-stat anim-up" style="--d:180ms;--ac:59,130,246">
                        <div class="rls-glow"></div>
                        <div class="rls-icon">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                            </svg>
                        </div>
                        <p class="rls-lbl">{{ t('teamInvestment') }}</p>
                        <p class="rls-val">{{ currencySymbol }}{{ formatCurrency(summary.totalInvestments) }}</p>
                        <p class="rls-sub">{{ t('totalVolume') }}</p>
                        <div class="rls-bar-wrap"><div class="rls-bar shimmer-bar" style="width:88%"></div></div>
                    </div>
                </div>

                <!-- Main Table Card -->
                <div class="rl-card anim-up" style="--d:240ms">
                    <div class="rl-card-head">
                        <div>
                            <h2 class="rl-card-title">{{ t('myReferrals') }}</h2>
                            <p class="rl-card-sub">{{ t('completeReferralNetwork') }}</p>
                        </div>
                        <div class="rl-filters">
                            <button
                                v-for="filter in ['all', 'active', 'inactive']"
                                :key="filter"
                                @click="changeFilter(filter)"
                                class="rl-filter-btn"
                                :class="{ 'rl-filter-btn--active': filterType === filter }">
                                {{ filter === 'all' ? t('all') : formatStatus(filter) }}
                            </button>
                        </div>
                    </div>

                    <!-- Desktop Table -->
                    <div class="rl-table-wrap">
                        <table class="rl-table">
                            <thead>
                            <tr>
                                <th>{{ t('member') }}</th>
                                <th>{{ t('level') }}</th>
                                <th>{{ t('totalEarned') }}</th>
                                <th>{{ t('investment') }}</th>
                                <th>{{ t('generations') }}</th>
                                <th>{{ t('status') }}</th>
                                <th>{{ t('joinedDate') }}</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="!filteredReferrals || filteredReferrals.length === 0">
                                <td colspan="7" class="rl-empty">
                                    <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                                    </svg>
                                    <p>{{ t('noReferralsFound') }}</p>
                                    <span>{{ t('invitePeopleToEarn') }}</span>
                                </td>
                            </tr>
                            <tr v-for="referral in filteredReferrals" :key="referral.id" class="rl-tr">
                                <td>
                                    <div class="rl-member">
                                        <div class="rl-avatar">{{ getInitials(referral.name) }}</div>
                                        <div>
                                            <p class="rl-member-name">{{ referral.name }}</p>
                                            <p class="rl-member-email">{{ referral.email }}</p>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span class="rl-level-badge">{{ t('level') }} {{ referral.level }}</span>
                                </td>
                                <td>
                                    <span class="rl-amount">{{ currencySymbol }}{{ formatCurrency(referral.total_commission_earned) }}</span>
                                </td>
                                <td>
                                    <span class="rl-text-white">{{ currencySymbol }}{{ formatCurrency(referral.total_referral_investment) }}</span>
                                </td>
                                <td>
                                    <span class="rl-text-dim">{{ referral.generation_count }}</span>
                                </td>
                                <td>
                                    <span class="rl-status-badge" :style="getStatusStyle(referral.status)">{{ formatStatus(referral.status) }}</span>
                                </td>
                                <td>
                                    <p class="rl-date">{{ formatDate(referral.joined_at) }}</p>
                                    <p class="rl-time">{{ formatTime(referral.joined_at) }}</p>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Mobile Cards -->
                    <div class="rl-mob-list">
                        <div v-if="!filteredReferrals || filteredReferrals.length === 0" class="rl-mob-empty">
                            <svg width="44" height="44" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                            </svg>
                            <p>{{ t('noReferralsFound') }}</p>
                        </div>
                        <div v-for="referral in filteredReferrals" :key="referral.id" class="rl-mob-item">
                            <div class="rl-mob-top">
                                <div class="rl-mob-user">
                                    <div class="rl-avatar">{{ getInitials(referral.name) }}</div>
                                    <div class="rl-mob-info">
                                        <p class="rl-member-name">{{ referral.name }}</p>
                                        <p class="rl-member-email">{{ referral.email }}</p>
                                    </div>
                                </div>
                                <span class="rl-status-badge" :style="getStatusStyle(referral.status)">{{ formatStatus(referral.status) }}</span>
                            </div>
                            <div class="rl-mob-grid">
                                <div class="rl-mob-cell">
                                    <span class="rl-mob-lbl">{{ t('earned') }}</span>
                                    <span class="rl-amount">{{ currencySymbol }}{{ formatCurrency(referral.total_commission_earned) }}</span>
                                </div>
                                <div class="rl-mob-cell">
                                    <span class="rl-mob-lbl">{{ t('investment') }}</span>
                                    <span class="rl-text-white">{{ currencySymbol }}{{ formatCurrency(referral.total_referral_investment) }}</span>
                                </div>
                            </div>
                            <div class="rl-mob-foot">
                                <span class="rl-text-dim">{{ t('level') }}: <b>{{ referral.level }}</b> &nbsp;·&nbsp; {{ t('generations') }}: <b>{{ referral.generation_count }}</b></span>
                                <span class="rl-time">{{ formatDate(referral.joined_at) }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Pagination -->
                    <div class="rl-pagination">
                        <p class="rl-page-info">
                            {{ t('showing') }} <b>{{ referrals.from || 0 }}</b> {{ t('to') }} <b>{{ referrals.to || 0 }}</b> {{ t('of') }} <b>{{ referrals.total || 0 }}</b> {{ t('results') }}
                        </p>
                        <div class="rl-page-btns">
                            <Link :href="referrals.prev_page_url" :disabled="!referrals.prev_page_url"
                                  class="rl-page-btn" :class="{ 'rl-page-btn--disabled': !referrals.prev_page_url }" preserve-scroll>
                                {{ t('previous') }}
                            </Link>
                            <Link v-for="pg in visiblePages" :key="pg" :href="getPageUrl(pg)"
                                  class="rl-page-num" :class="{ 'rl-page-num--active': pg === referrals.current_page }" preserve-scroll>
                                {{ pg }}
                            </Link>
                            <Link :href="referrals.next_page_url" :disabled="!referrals.next_page_url"
                                  class="rl-page-btn" :class="{ 'rl-page-btn--disabled': !referrals.next_page_url }" preserve-scroll>
                                {{ t('next') }}
                            </Link>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </UserLayout>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue';
import {usePage, Link} from '@inertiajs/vue3';
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue";
import {useToast} from "@/composables/useToast.js";
import {useTranslation} from '@/composables/useTranslation';

const props = defineProps({
    referrals: {
        type: Object,
        required: true
    },
    summary: {
        type: Object,
        required: true
    }
});

const {showToast} = useToast();
const {t} = useTranslation();
const page = usePage();

const filterType = ref('all');

const primaryColor = computed(() => page.props.primaryColor || '#86efac');
const currencySymbol = computed(() => page.props.currencySymbol || '$');

const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') return {r: 134, g: 239, b: 172};
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : {r: 134, g: 239, b: 172};
};

const rgbToHex = (r, g, b) => "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex);
    return rgbToHex(
        Math.min(255, Math.max(0, rgb.r + amount)),
        Math.min(255, Math.max(0, rgb.g + amount)),
        Math.min(255, Math.max(0, rgb.b + amount))
    );
};

const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryLight: adjustColor(primaryColor.value, 40),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}));

const filteredReferrals = computed(() => {
    if (filterType.value === 'all') return props.referrals.data || [];
    return (props.referrals.data || []).filter(r => r.status === filterType.value);
});

const visiblePages = computed(() => {
    const pages = [];
    const current = props.referrals.current_page || 1;
    const last = props.referrals.last_page || 1;

    for (let i = 1; i <= last; i++) {
        if (i === 1 || i === last || Math.abs(i - current) <= 1) {
            pages.push(i);
        }
    }
    return pages;
});

const getPageUrl = (page) => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);
    return url.pathname + url.search;
};

const changeFilter = (filter) => {
    filterType.value = filter;
};

const formatCurrency = (amount) => {
    const number = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(number);
};

const formatDate = (dateString) => {
    if (!dateString) return t('notAvailable');
    try {
        return new Date(dateString).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
    } catch {
        return t('invalidDate');
    }
};

const formatTime = (dateString) => {
    if (!dateString) return t('notAvailable');
    try {
        return new Date(dateString).toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit'});
    } catch {
        return t('invalidTime');
    }
};

const formatStatus = (status) => {
    if (!status || typeof status !== 'string') return t('unknown');
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const getStatusStyle = (status) => {
    if (!status) return {
        backgroundColor: 'rgba(148, 163, 184, 0.2)',
        borderColor: 'rgba(148, 163, 184, 0.3)',
        color: '#94a3b8'
    };

    switch (status.toLowerCase()) {
        case 'active':
            return {
                backgroundColor: `rgba(${themeColors.value.primaryRgb}, 0.2)`,
                borderColor: `rgba(${themeColors.value.primaryRgb}, 0.3)`,
                color: themeColors.value.primary
            };
        case 'inactive':
            return {
                backgroundColor: 'rgba(148, 163, 184, 0.2)',
                borderColor: 'rgba(148, 163, 184, 0.3)',
                color: '#94a3b8'
            };
        default:
            return {
                backgroundColor: 'rgba(148, 163, 184, 0.2)',
                borderColor: 'rgba(148, 163, 184, 0.3)',
                color: '#94a3b8'
            };
    }
};

const getInitials = (name) => {
    if (!name) return 'NA';
    const parts = name.split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

onMounted(() => {
    try {
        const flash = page.props.flash;
        if (flash?.success) showToast(flash.success, 'success');
        if (flash?.error) showToast(flash.error, 'error');
        if (flash?.warning) showToast(flash.warning, 'warning');
        if (flash?.info) showToast(flash.info, 'info');
    } catch (error) {
        console.error('Component mount error:', error);
    }
});
</script>

<style scoped>
.anim-up { animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both; animation-delay: var(--d,0ms); }
@keyframes slideUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:none} }

.rl-wrap { width:100%; }
.rl-inner { max-width:1400px; margin:0 auto; padding:20px 16px; display:flex; flex-direction:column; gap:14px; }
@media(min-width:768px) { .rl-inner { padding:24px; gap:16px; } }
@media(min-width:1024px) { .rl-inner { padding:28px 32px; } }

/* Welcome */
.rl-welcome { background:rgba(10,4,22,0.97); border:1px solid rgba(167,93,237,0.13); border-radius:16px; padding:16px 20px; display:flex; align-items:center; justify-content:space-between; gap:12px; position:relative; overflow:hidden; flex-wrap:wrap; }
.rl-welcome::before { content:''; position:absolute; top:0; left:14%; right:14%; height:1px; background:linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent); }
.rlw-left { display:flex; align-items:center; gap:12px; }
.rlw-icon { width:40px; height:40px; border-radius:11px; background:rgba(167,93,237,0.14); border:1px solid rgba(167,93,237,0.26); display:flex; align-items:center; justify-content:center; color:#a75ded; flex-shrink:0; }
.rlw-eyebrow { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.08em; color:rgba(255,255,255,0.36); margin:0 0 2px; }
.rlw-title { font-size:18px; font-weight:700; color:#fff; margin:0; }
.rlw-chips { display:flex; gap:7px; flex-wrap:wrap; }
.rlw-chip { background:rgba(167,93,237,0.09); border:1px solid rgba(167,93,237,0.17); border-radius:9px; padding:6px 12px; display:flex; flex-direction:column; align-items:center; }
.rlw-chip--green { background:rgba(52,211,153,0.09); border-color:rgba(52,211,153,0.2); }
.rlw-chip--green .rlw-chip-val { color:#34d399; }
.rlw-chip--blue { background:rgba(59,130,246,0.09); border-color:rgba(59,130,246,0.2); }
.rlw-chip--blue .rlw-chip-val { color:#60a5fa; }
.rlw-chip-val { font-size:13px; font-weight:700; color:#a75ded; }
.rlw-chip-lbl { font-size:9px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:rgba(255,255,255,0.38); margin-top:2px; }

/* Stats */
.rl-stats { display:grid; grid-template-columns:repeat(2,1fr); gap:10px; }
@media(min-width:768px) { .rl-stats { grid-template-columns:repeat(4,1fr); } }

.rl-stat { background:rgba(10,4,22,0.97); border:1px solid rgba(var(--ac),0.17); border-radius:16px; padding:14px; position:relative; overflow:hidden; }
.rl-stat::before { content:''; position:absolute; top:0; left:10%; right:10%; height:1px; background:linear-gradient(90deg,transparent,rgba(var(--ac),0.4),transparent); }
.rls-glow { position:absolute; top:-18px; right:-18px; width:70px; height:70px; border-radius:50%; background:radial-gradient(circle,rgba(var(--ac),0.16) 0%,transparent 70%); pointer-events:none; }
.rls-icon { width:30px; height:30px; border-radius:8px; background:rgba(var(--ac),0.13); border:1px solid rgba(var(--ac),0.22); display:flex; align-items:center; justify-content:center; color:rgb(var(--ac)); margin-bottom:9px; }
.rls-lbl { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.07em; color:rgba(255,255,255,0.4); margin:0 0 3px; }
.rls-val { font-size:20px; font-weight:800; color:#fff; margin:0 0 2px; line-height:1.1; }
.rls-sub { font-size:11px; font-weight:600; color:rgb(var(--ac)); margin:0 0 8px; }
.rls-bar-wrap { height:3px; background:rgba(255,255,255,0.06); border-radius:99px; overflow:hidden; }
.rls-bar { height:3px; border-radius:99px; background:rgba(var(--ac),0.65); }

/* Shimmer */
.shimmer-bar { position:relative; overflow:hidden; }
.shimmer-bar::after { content:''; position:absolute; inset:0; background:linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.18) 50%,transparent 100%); animation:shimmer 2s infinite; }
@keyframes shimmer { from{transform:translateX(-100%)} to{transform:translateX(100%)} }

/* Main Card */
.rl-card { background:rgba(10,4,22,0.97); border:1px solid rgba(167,93,237,0.13); border-radius:16px; overflow:hidden; position:relative; }
.rl-card::before { content:''; position:absolute; top:0; left:14%; right:14%; height:1px; background:linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent); z-index:1; }

.rl-card-head { padding:18px 20px; border-bottom:1px solid rgba(167,93,237,0.1); display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.rl-card-title { font-size:15px; font-weight:700; color:#fff; margin:0 0 3px; }
.rl-card-sub { font-size:12px; color:rgba(255,255,255,0.4); margin:0; }
.rl-filters { display:flex; gap:6px; flex-wrap:wrap; }
.rl-filter-btn { padding:6px 12px; border-radius:8px; font-size:12px; font-weight:600; border:none; cursor:pointer; background:rgba(255,255,255,0.06); color:rgba(255,255,255,0.6); transition:all .2s; }
.rl-filter-btn--active { background:rgba(167,93,237,0.22); color:#a75ded; border:1px solid rgba(167,93,237,0.3); }

/* Desktop Table */
.rl-table-wrap { display:none; overflow-x:auto; }
@media(min-width:900px) { .rl-table-wrap { display:block; } }

.rl-table { width:100%; border-collapse:collapse; }
.rl-table thead { background:rgba(167,93,237,0.06); }
.rl-table th { padding:13px 18px; text-align:left; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; color:rgba(255,255,255,0.45); white-space:nowrap; }
.rl-tr { border-top:1px solid rgba(167,93,237,0.07); transition:background .15s; }
.rl-tr:hover { background:rgba(167,93,237,0.04); }
.rl-table td { padding:14px 18px; vertical-align:middle; }

.rl-member { display:flex; align-items:center; gap:10px; }
.rl-avatar { width:34px; height:34px; border-radius:9px; background:rgba(167,93,237,0.14); border:1px solid rgba(167,93,237,0.22); display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:800; color:#a75ded; flex-shrink:0; }
.rl-member-name { font-size:13px; font-weight:600; color:#fff; margin:0 0 2px; }
.rl-member-email { font-size:11px; color:rgba(255,255,255,0.38); margin:0; max-width:180px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

.rl-level-badge { display:inline-flex; padding:4px 10px; border-radius:7px; font-size:11px; font-weight:600; background:rgba(167,93,237,0.14); color:#a75ded; }
.rl-amount { font-size:13px; font-weight:700; color:#a75ded; }
.rl-text-white { font-size:13px; font-weight:600; color:#fff; }
.rl-text-dim { font-size:13px; color:rgba(255,255,255,0.5); }
.rl-status-badge { display:inline-flex; padding:4px 10px; border-radius:99px; font-size:11px; font-weight:600; border:1px solid transparent; }
.rl-date { font-size:12px; color:rgba(255,255,255,0.65); margin:0 0 1px; }
.rl-time { font-size:11px; color:rgba(255,255,255,0.35); margin:0; }

.rl-empty { text-align:center; padding:60px 20px; }
.rl-empty svg { color:rgba(167,93,237,0.25); margin:0 auto 12px; display:block; }
.rl-empty p { font-size:15px; font-weight:600; color:rgba(255,255,255,0.5); margin:0 0 4px; }
.rl-empty span { font-size:12px; color:rgba(255,255,255,0.28); }

/* Mobile List */
.rl-mob-list { display:flex; flex-direction:column; }
@media(min-width:900px) { .rl-mob-list { display:none; } }

.rl-mob-empty { text-align:center; padding:50px 20px; }
.rl-mob-empty svg { color:rgba(167,93,237,0.25); margin:0 auto 10px; display:block; }
.rl-mob-empty p { font-size:14px; font-weight:600; color:rgba(255,255,255,0.45); margin:0; }

.rl-mob-item { padding:16px 20px; border-top:1px solid rgba(167,93,237,0.08); display:flex; flex-direction:column; gap:10px; }
.rl-mob-item:hover { background:rgba(167,93,237,0.03); }
.rl-mob-top { display:flex; align-items:center; justify-content:space-between; gap:10px; }
.rl-mob-user { display:flex; align-items:center; gap:10px; flex:1; min-width:0; }
.rl-mob-info { flex:1; min-width:0; }
.rl-mob-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
.rl-mob-cell { background:rgba(255,255,255,0.03); border:1px solid rgba(167,93,237,0.08); border-radius:9px; padding:10px 12px; }
.rl-mob-lbl { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:rgba(255,255,255,0.38); display:block; margin-bottom:4px; }
.rl-mob-foot { display:flex; justify-content:space-between; align-items:center; }
.rl-mob-foot .rl-text-dim { font-size:11px; }
.rl-mob-foot b { color:rgba(255,255,255,0.7); font-weight:600; }

/* Pagination */
.rl-pagination { padding:16px 20px; border-top:1px solid rgba(167,93,237,0.1); display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.rl-page-info { font-size:12px; color:rgba(255,255,255,0.4); }
.rl-page-info b { color:rgba(255,255,255,0.75); font-weight:600; }
.rl-page-btns { display:flex; gap:6px; flex-wrap:wrap; align-items:center; }
.rl-page-btn { padding:7px 14px; border-radius:8px; font-size:12px; font-weight:600; background:rgba(255,255,255,0.06); color:rgba(255,255,255,0.65); text-decoration:none; transition:background .2s; }
.rl-page-btn:hover { background:rgba(167,93,237,0.14); color:#a75ded; }
.rl-page-btn--disabled { opacity:.35; pointer-events:none; }
.rl-page-num { width:32px; height:32px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:600; background:rgba(255,255,255,0.06); color:rgba(255,255,255,0.65); text-decoration:none; transition:all .2s; }
.rl-page-num--active { background:rgba(167,93,237,0.25); color:#a75ded; border:1px solid rgba(167,93,237,0.35); }

@media(prefers-reduced-motion:reduce) {
    .anim-up { animation:none; }
    .shimmer-bar::after { animation:none; }
}
</style>
